import { useEffect, useMemo, useState } from "react";

type Token = { text: string; className?: string };
type Line = { tokens: Token[]; highlight?: boolean };

const KEY = "text-primary";
const STR = "text-foreground";
const FN = "text-primary font-semibold";

/** Detect the programming language for a course by its name. */
export function detectCourseLanguage(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("c++")) return "cpp";
  if (n.includes("c#")) return "csharp";
  if (n.includes("php") || n.includes("laravel") || n.includes("wordpress")) return "php";
  if (n.includes("sql") || n.includes("\u057f\u057e\u0575\u0561\u056c\u0576\u0565\u0580\u056b \u0562\u0561\u0566")) return "sql";
  if (n.includes("swift") || n.includes("ios")) return "swift";
  if (n.includes("kotlin") || n.includes("android")) return "kotlin";
  if (n.includes("flutter") || n.includes("dart")) return "dart";
  if (n.includes("golang") || n === "go") return "go";
  if (n.includes("node") || n.includes("react") || n.includes("vue") || n.includes("javascript") || n.includes("js")) return "javascript";
  if (n.includes("html") || n.includes("css") || n.includes("\u057e\u0565\u0562 \u056e\u0580\u0561\u0563\u0580\u0561\u057e\u0578\u0580")) return "html";
  if (n.includes("front-end") || n.includes("back-end")) return "javascript";
  if (n.includes("angular") || n.includes("typescript") || n.includes("qa") || n.includes("test")) return "typescript";
  if (n.includes("java")) return "java";
  if (n.includes("python") || n.includes("\u0561\u0580\u0570\u0565\u057d\u057f\u0561\u056f\u0561\u0576") || n.includes("\u056f\u056b\u0562\u0565\u057c") || n.includes("ai") || n.includes("data")) return "python";
  if (n.includes("\u056d\u0561\u0572\u0565\u0580\u056b") || n.includes("unity")) return "csharp";
  return "typescript";
}

export function courseCodeFileName(name: string): string {
  switch (detectCourseLanguage(name)) {
    case "cpp": return "Course.cpp";
    case "csharp": return "Course.cs";
    case "javascript": return "Course.js";
    case "java": return "Course.java";
    case "python": return "Course.py";
    case "php": return "Course.php";
    case "sql": return "course.sql";
    case "swift": return "Course.swift";
    case "kotlin": return "Course.kt";
    case "dart": return "course.dart";
    case "go": return "course.go";
    case "html": return "index.html";
    default: return "Course.ts";
  }
}

const TYPE = "text-primary/80";

function buildLines(name: string): Line[] {
  const lang = detectCourseLanguage(name);
  const q = `"${name}"`;
  switch (lang) {
    case "csharp":
      return [
        { tokens: [{ text: "using", className: KEY }, { text: " System;" }] },
        { tokens: [{ text: "class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " {" }] },
        { tokens: [{ text: "  static void", className: KEY }, { text: " " }, { text: "Main", className: FN }, { text: "() {" }] },
        { tokens: [{ text: "    " }, { text: "Console", className: TYPE }, { text: "." }, { text: "WriteLine", className: FN }, { text: "(" }, { text: q, className: STR }, { text: ");" }], highlight: true },
        { tokens: [{ text: "  }" }] },
        { tokens: [{ text: "}" }] },
      ];
    case "python":
      return [
        { tokens: [{ text: "class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: ":" }] },
        { tokens: [{ text: "    def", className: KEY }, { text: " " }, { text: "__init__", className: FN }, { text: "(self, name):" }] },
        { tokens: [{ text: "        self.name = name" }] },
        { tokens: [{ text: "print", className: FN }, { text: "(" }, { text: "Course", className: TYPE }, { text: "(" }, { text: q, className: STR }, { text: ").name)" }], highlight: true },
      ];
    case "javascript":
      return [
        { tokens: [{ text: "class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " {" }] },
        { tokens: [{ text: "  constructor", className: FN }, { text: "(name) { " }, { text: "this", className: KEY }, { text: ".name = name; }" }] },
        { tokens: [{ text: "}" }] },
        { tokens: [{ text: "console", className: TYPE }, { text: "." }, { text: "log", className: FN }, { text: "(" }, { text: "new", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: "(" }, { text: q, className: STR }, { text: ").name);" }], highlight: true },
      ];
    case "cpp":
      return [
        { tokens: [{ text: "#include", className: KEY }, { text: " <iostream>" }] },
        { tokens: [{ text: "int", className: KEY }, { text: " " }, { text: "main", className: FN }, { text: "() {" }] },
        { tokens: [{ text: "  std::cout << " }, { text: q, className: STR }, { text: " << std::endl;" }], highlight: true },
        { tokens: [{ text: "  " }, { text: "return", className: KEY }, { text: " 0;" }] },
        { tokens: [{ text: "}" }] },
      ];
    case "java":
      return [
        { tokens: [{ text: "public class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " {" }] },
        { tokens: [{ text: "  public static void", className: KEY }, { text: " " }, { text: "main", className: FN }, { text: "(String[] args) {" }] },
        { tokens: [{ text: "    System.out." }, { text: "println", className: FN }, { text: "(" }, { text: q, className: STR }, { text: ");" }], highlight: true },
        { tokens: [{ text: "  }" }] },
        { tokens: [{ text: "}" }] },
      ];
    case "php":
      return [
        { tokens: [{ text: "<?php", className: KEY }] },
        { tokens: [{ text: "class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " { public $name; }" }] },
        { tokens: [{ text: "$course = " }, { text: "new", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: "();" }] },
        { tokens: [{ text: "$course->name = " }, { text: q, className: STR }, { text: ";" }] },
        { tokens: [{ text: "echo", className: KEY }, { text: " $course->name;" }], highlight: true },
      ];
    case "sql":
      return [
        { tokens: [{ text: "CREATE TABLE", className: KEY }, { text: " courses (" }] },
        { tokens: [{ text: "  id " }, { text: "SERIAL PRIMARY KEY", className: TYPE }, { text: "," }] },
        { tokens: [{ text: "  name " }, { text: "TEXT NOT NULL", className: TYPE }, { text: ");" }] },
        { tokens: [{ text: "INSERT INTO", className: KEY }, { text: " courses (name) " }, { text: "VALUES", className: KEY }, { text: " (" }, { text: q, className: STR }, { text: ");" }], highlight: true },
      ];
    case "swift":
      return [
        { tokens: [{ text: "import", className: KEY }, { text: " Foundation" }] },
        { tokens: [{ text: "struct", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " { " }, { text: "let", className: KEY }, { text: " name: " }, { text: "String", className: TYPE }, { text: " }" }] },
        { tokens: [{ text: "let", className: KEY }, { text: " course = " }, { text: "Course", className: TYPE }, { text: "(name: " }, { text: q, className: STR }, { text: ")" }] },
        { tokens: [{ text: "print", className: FN }, { text: "(course.name)" }], highlight: true },
      ];
    case "kotlin":
      return [
        { tokens: [{ text: "data class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: "(" }, { text: "val", className: KEY }, { text: " name: " }, { text: "String", className: TYPE }, { text: ")" }] },
        { tokens: [{ text: "fun", className: KEY }, { text: " " }, { text: "main", className: FN }, { text: "() {" }] },
        { tokens: [{ text: "    " }, { text: "println", className: FN }, { text: "(" }, { text: "Course", className: TYPE }, { text: "(" }, { text: q, className: STR }, { text: ").name)" }], highlight: true },
        { tokens: [{ text: "}" }] },
      ];
    case "dart":
      return [
        { tokens: [{ text: "class", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " { " }, { text: "final", className: KEY }, { text: " " }, { text: "String", className: TYPE }, { text: " name; Course(" }, { text: "this", className: KEY }, { text: ".name); }" }] },
        { tokens: [{ text: "void", className: KEY }, { text: " " }, { text: "main", className: FN }, { text: "() {" }] },
        { tokens: [{ text: "  " }, { text: "print", className: FN }, { text: "(" }, { text: "Course", className: TYPE }, { text: "(" }, { text: q, className: STR }, { text: ").name);" }], highlight: true },
        { tokens: [{ text: "}" }] },
      ];
    case "go":
      return [
        { tokens: [{ text: "package", className: KEY }, { text: " main" }] },
        { tokens: [{ text: "import", className: KEY }, { text: " " }, { text: "\"fmt\"", className: STR }] },
        { tokens: [{ text: "func", className: KEY }, { text: " " }, { text: "main", className: FN }, { text: "() {" }] },
        { tokens: [{ text: "  fmt." }, { text: "Println", className: FN }, { text: "(" }, { text: q, className: STR }, { text: ")" }], highlight: true },
        { tokens: [{ text: "}" }] },
      ];
    case "html":
      return [
        { tokens: [{ text: "<!DOCTYPE html>", className: KEY }] },
        { tokens: [{ text: "<html", className: TYPE }, { text: " lang=" }, { text: "\"hy\"", className: STR }, { text: ">" }] },
        { tokens: [{ text: "  <h1", className: TYPE }, { text: ">" }, { text: name, className: STR }, { text: "</h1>" }], highlight: true },
        { tokens: [{ text: "</html>", className: TYPE }] },
      ];
    default:
      return [
        { tokens: [{ text: "type", className: KEY }, { text: " " }, { text: "Course", className: TYPE }, { text: " = { name: " }, { text: "string", className: TYPE }, { text: " };" }] },
        { tokens: [{ text: "const", className: KEY }, { text: " course: " }, { text: "Course", className: TYPE }, { text: " = { name: " }, { text: q, className: STR }, { text: " };" }] },
        { tokens: [{ text: "console." }, { text: "log", className: FN }, { text: "(course.name);" }], highlight: true },
      ];
  }
}

const TYPE_SPEED = 26;
const LINE_PAUSE = 300;
const RESTART_PAUSE = 4200;
const START_DELAY = 600;

export function CourseCodePanel({ courseName }: { courseName: string }) {
  const lines = useMemo(() => buildLines(courseName), [courseName]);
  const lineTexts = useMemo(
    () => lines.map((line) => line.tokens.map((t) => t.text).join("")),
    [lines]
  );
  const lineStarts = useMemo(() => {
    const starts: number[] = [];
    let acc = 0;
    for (const text of lineTexts) {
      starts.push(acc);
      acc += text.length;
    }
    return starts;
  }, [lineTexts]);
  const totalChars = useMemo(
    () => lineTexts.reduce((sum, text) => sum + text.length, 0),
    [lineTexts]
  );

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const [typed, setTyped] = useState(reducedMotion ? totalChars : 0);

  useEffect(() => {
    if (reducedMotion) {
      setTyped(totalChars);
      return;
    }
    let cancelled = false;
    let timeout: number;
    let i = 0;

    const step = () => {
      if (cancelled) return;
      i += 1;
      if (i > totalChars) {
        timeout = window.setTimeout(() => {
          if (cancelled) return;
          i = 0;
          setTyped(0);
          timeout = window.setTimeout(step, START_DELAY);
        }, RESTART_PAUSE);
        return;
      }
      setTyped(i);
      const atLineEnd = lineTexts.some((text, idx) => lineStarts[idx] + text.length === i);
      timeout = window.setTimeout(step, atLineEnd ? LINE_PAUSE : TYPE_SPEED);
    };

    timeout = window.setTimeout(step, START_DELAY);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [reducedMotion, totalChars, lineTexts, lineStarts]);

  // The line the cursor currently sits on
  const activeLine = useMemo(() => {
    for (let idx = 0; idx < lineTexts.length; idx++) {
      if (typed < lineStarts[idx] + lineTexts[idx].length) return idx;
    }
    return lineTexts.length - 1;
  }, [typed, lineStarts, lineTexts]);

  return (
    <div className="space-y-4 font-mono text-xs leading-6 text-muted-foreground sm:text-sm">
      {lines.map((line, idx) => {
        const start = lineStarts[idx];
        let visible = Math.max(0, Math.min(typed - start, lineTexts[idx].length));
        const rendered: React.ReactNode[] = [];
        let tokenKey = 0;
        for (const token of line.tokens) {
          if (visible <= 0) break;
          const slice = token.text.slice(0, visible);
          visible -= slice.length;
          rendered.push(
            <span key={tokenKey++} className={token.className}>
              {slice}
            </span>
          );
        }
        return (
          <p
            key={idx}
            className={
              line.highlight
                ? "rounded-md border-l-2 border-primary bg-primary/10 px-3 py-1"
                : undefined
            }
          >
            <span className="mr-4 text-muted-foreground/50">
              {String(idx + 1).padStart(2, "0")}
            </span>
            {rendered}
            {!reducedMotion && idx === activeLine && (
              <span
                aria-hidden="true"
                className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 animate-pulse bg-primary"
              />
            )}
          </p>
        );
      })}
    </div>
  );
}
